import React, { memo, useCallback, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Container, Button } from 'reactstrap';
import { Link } from 'react-router-dom';
import { capitalize } from 'lodash-es';
import { FirstProduct } from './FirstProduct';

const formatAnswer = value => {
  if (Array.isArray(value)) {
    return value.map(formatAnswer).join(', ');
  }

  return value ? capitalize(String(value).replace(/-/g, ' ')) : undefined;
};

const formatBrew = grind => {
  if (!grind) {
    return undefined;
  }

  const [, brew] = String(grind).split(',');
  return (brew || grind).trim();
};

const PersonalizedProductComponent = ({
  firstVariant: originalFirstVariant,
  personalized,
  firstProduct: originalFirstProduct,
  onSignUp,
  buttonText,
  grind,
  alternativeProducts,
  hideAdvancedOptions,
  error,
  customRotationShopFilter,
  expressCheckoutButton,
  selectedProduct,
  selectedVariant,
  setSelectedProduct,
  setSelectedVariant,
}) => {
  const [isLoading, setLoading] = useState(false);
  const [loadingProductId, setLoadingProductId] = useState(null);

  const isComponentMounted = useRef(false);

  useEffect(() => {
    isComponentMounted.current = true;
    return () => {
      isComponentMounted.current = false;
    };
  }, []);

  const selectProduct = useCallback(
    ({ product, variant }) => () => {
      setSelectedProduct(product);
      setSelectedVariant(variant);
    },
    [setSelectedProduct, setSelectedVariant]
  );

  const handleSignup = useCallback(async () => {
    if (isComponentMounted.current) {
      setLoading(true);
      setLoadingProductId(null);
    }
    await onSignUp({ first_product: { product: selectedProduct?._id, variant: selectedVariant?._id } });
    if (isComponentMounted.current) {
      setLoading(false);
    }
  }, [onSignUp, selectedProduct, selectedVariant]);

  const handleStart = useCallback(
    async ({ product, variant }) => {
      if (isComponentMounted.current) {
        setSelectedProduct(product);
        setSelectedVariant(variant);
        setLoading(true);
        setLoadingProductId(product?._id);
      }

      await onSignUp({ first_product: { product: product?._id, variant: variant?._id } });

      if (isComponentMounted.current) {
        setLoading(false);
        setLoadingProductId(null);
      }
    },
    [onSignUp, setSelectedProduct, setSelectedVariant]
  );

  const firstProductOptions = [
    { product: originalFirstProduct, variant: originalFirstVariant },
    ...(alternativeProducts || []),
  ].filter(({ product, variant }) => product && variant);

  const pills = [
    { label: 'Roast', value: formatAnswer(personalized.roast) },
    { label: 'Brew', value: formatBrew(grind) },
  ].filter(({ value }) => value);

  return (
    <Container className="quiz-summary-container">
      <div className="quiz-summary-hero">
        <h1>Your rotation is ready.</h1>
        <div className="quiz-summary-pills">
          {pills.map(({ label, value }) => (
            <span key={label}>
              <strong>{label}</strong> {value}
            </span>
          ))}
        </div>
      </div>

      <div className="quiz-summary-intro">
        <div>
          <h2>Pick your first coffee</h2>
          <p>Bottomless keeps you stocked on brews in your rotation as you run low.</p>
        </div>
        <span>We will keep the rotation going from there.</span>
      </div>

      <Row className="quiz-summary-product-grid">
        {firstProductOptions.map(({ product, variant }) => (
          <Col xs="12" md="4" key={product._id}>
            <FirstProduct
              product={product}
              variant={variant}
              onClick={selectProduct({ product, variant })}
              onStart={handleStart}
              active={product._id === selectedProduct._id}
              isLoading={isLoading && loadingProductId === product._id}
            />
          </Col>
        ))}
      </Row>

      <div className="quiz-summary-footer">
        <Button
          color="primary"
          onClick={handleSignup}
          disabled={isLoading}
          block
          className="quiz-summary-checkout-button"
        >
          {isLoading && !loadingProductId ? 'Processing...' : buttonText}
        </Button>
        {expressCheckoutButton}
        <div className="quiz-summary-links">
          {!hideAdvancedOptions && <Link to="/filter_checkout/advanced">See Advanced Options</Link>}
          <Link
            to={`/public_shop${
              customRotationShopFilter
                ? `${customRotationShopFilter}&back=/filter_checkout?finished=true`
                : '?back=/filter_checkout?finished=true'
            }`}
          >
            Shop Matching Coffee
          </Link>
        </div>
        {error && <div className="text-danger">{error}</div>}
      </div>
    </Container>
  );
};

PersonalizedProductComponent.propTypes = {
  product: PropTypes.shape({
    name: PropTypes.string.isRequired,
    vendor_name: PropTypes.string.isRequired,
    vendor_id: PropTypes.string,
    description: PropTypes.string,
  }),
  firstProduct: PropTypes.object.isRequired,
  personalized: PropTypes.object.isRequired,
  variant: PropTypes.shape({
    size: PropTypes.number.isRequired,
    price: PropTypes.number.isRequired,
  }).isRequired,
  firstVariant: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    size: PropTypes.number.isRequired,
    price: PropTypes.number.isRequired,
  }).isRequired,
  onSignUp: PropTypes.func.isRequired,
  buttonText: PropTypes.string,
  grind: PropTypes.string,
  pricingRule: PropTypes.object.isRequired,
  pricing: PropTypes.shape({
    price: PropTypes.number.isRequired,
  }).isRequired,
  alternativeProducts: PropTypes.array,
  dateOverride: PropTypes.string,
  vendor: PropTypes.object,
  hideAdvancedOptions: PropTypes.bool,
  error: PropTypes.string,
  largeButton: PropTypes.bool,
  customRotationShopFilter: PropTypes.string,
  isQuizCompleted: PropTypes.bool,
  isModalOpen: PropTypes.bool,
  expressCheckoutButton: PropTypes.node,
  selectedProduct: PropTypes.object.isRequired,
  selectedVariant: PropTypes.object.isRequired,
  setSelectedProduct: PropTypes.func.isRequired,
  setSelectedVariant: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
};

PersonalizedProductComponent.defaultProps = {
  buttonText: 'Start Coffee Journey',
  hideAdvancedOptions: false,
  filterCheckoutShop: false,
  isQuizCompleted: false,
  isModalOpen: false,
};

export const PersonalizedProduct = memo(PersonalizedProductComponent);
